import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Platform,
  StyleSheet,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Extrapolation,
  cancelAnimation,
  interpolate,
  runOnJS,
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { layoutLtrStyle } from '../../../shared/i18n/rtl';
import { triggerHaptic } from '../../../shared/utils/haptics';
import { Paginator } from './Paginator';
import { STACK_CAROUSEL_INACTIVE_SCALE, STACK_CAROUSEL_PARALLAX } from '../../../shared/theme/studio/studioMotion';

const TOUCH_LAYER_Z = 2000;

/** Any clear horizontal move past this commits one page (distance does not matter beyond direction). */
const SWIPE_COMMIT_PX = 20;
/** Fallback when translation is tiny but fling direction is clear. */
const SWIPE_VELOCITY = 180;
/** Settle timing — no spring bounce / multi-page momentum. */
const SETTLE_MS = 220;
/** Neighbor cards kept mounted around the active index. */
const NEIGHBOR_WINDOW = 2;

function clamp(n: number, min: number, max: number) {
  'worklet';
  return Math.max(min, Math.min(max, n));
}

const defaultCardShell: ViewStyle = {
  backgroundColor: '#fff',
  borderRadius: 25,
  flex: 1,
  overflow: 'hidden',
  ...Platform.select({
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
    },
    android: { elevation: 0 },
    default: {},
  }),
};

type AnimatedStackedCardProps = {
  index: number;
  progress: SharedValue<number>;
  cardWidth: number;
  cardHeight: number;
  cardHeightSV?: SharedValue<number>;
  cardLeft: number;
  cardShellStyle?: ViewStyle;
  children: React.ReactNode;
};

const AnimatedStackedCard = ({
  index,
  progress,
  cardWidth,
  cardHeight,
  cardHeightSV,
  cardLeft,
  cardShellStyle,
  children,
}: AnimatedStackedCardProps) => {
  const animatedStyle = useAnimatedStyle(() => {
    const position = (progress.value - index) * cardWidth;
    const translate_x = -position * STACK_CAROUSEL_PARALLAX;
    const scale = interpolate(Math.abs(position), [0, cardWidth], [1, STACK_CAROUSEL_INACTIVE_SCALE], Extrapolation.CLAMP);
    const opacity = interpolate(Math.abs(position), [0, cardWidth * 0.9, cardWidth], [1, 0.78, 0.45], Extrapolation.CLAMP);
    const z_index = Math.round(900 - (Math.abs(position) / Math.max(cardWidth, 1)) * 400);
    const height = cardHeightSV != null ? cardHeightSV.value : cardHeight;
    return {
      height,
      transform: [{ translateX: translate_x }, { scale }],
      opacity,
      zIndex: z_index,
    };
  });

  return (
    <Animated.View
      collapsable={false}
      pointerEvents="none"
      style={[
        layoutStyles.cardContainer,
        {
          left: cardLeft,
          width: cardWidth,
          height: cardHeight,
        },
        animatedStyle,
      ]}>
      <View style={[defaultCardShell, cardShellStyle]} collapsable={false}>
        {children}
      </View>
    </Animated.View>
  );
};

const layoutStyles = StyleSheet.create({
  cardContainer: { position: 'absolute' as const },
  root: {
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
    position: 'relative' as const,
    width: '100%' as const,
  },
});

export type StackedCarouselProps<T> = {
  data: readonly T[];
  keyExtractor: (item: T, index: number) => string;
  renderCard: (item: T, index: number) => React.ReactNode;
  /** Touch layer for the active page. Omit for swipe-only. */
  renderScrollOverlay?: (item: T, index: number) => React.ReactNode;
  cardWidth: number;
  cardHeight: number;
  /** When set, card / stage height follows this shared value (width unchanged). */
  cardHeightSV?: SharedValue<number>;
  /** Kept for API compatibility; stack is horizontal-only. */
  stackOffset?: number;
  activeIndex: number;
  onIndexChange: (index: number) => void;
  showPaginator?: boolean;
  paginatorVisibleDots?: number;
  paginatorDotSize?: number;
  paginatorSpacing?: number;
  slidePaginatorGap?: number;
  paginatorRowStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  listStyle?: StyleProp<ViewStyle>;
  cardShellStyle?: ViewStyle;
  hapticOnIndexCommit?: boolean;
  extraData?: unknown;
};

/**
 * One-swipe → one-page stacked carousel.
 * LTR layout island (stable index ↔ offset). No FlatList paging / momentum settle.
 */
export function StackedCarousel<T>({
  data,
  keyExtractor,
  renderCard,
  renderScrollOverlay,
  cardWidth,
  cardHeight,
  cardHeightSV,
  activeIndex,
  onIndexChange,
  showPaginator = true,
  paginatorVisibleDots = 5,
  paginatorDotSize = 10,
  paginatorSpacing = 10,
  slidePaginatorGap = 6,
  paginatorRowStyle,
  style,
  listStyle,
  cardShellStyle,
  hapticOnIndexCommit = true,
  extraData: _extraData,
}: StackedCarouselProps<T>) {
  const { width: screen_width } = useWindowDimensions();
  const safe_len = data.length;
  const clamped_active = safe_len > 0 ? clamp(activeIndex, 0, safe_len - 1) : 0;

  const index_sv = useSharedValue(clamped_active);
  const drag_x = useSharedValue(0);
  const settle_lock = useSharedValue(0);
  const last_committed = useRef(clamped_active);
  const has_mounted = useRef(false);
  const ignore_prop_sync_until = useRef(0);

  const progress = useDerivedValue(() => {
    const w = Math.max(cardWidth, 1);
    return index_sv.value - drag_x.value / w;
  });

  const commitIndex = useCallback(
    (next: number) => {
      if (safe_len === 0) {
        return;
      }
      const clamped = clamp(next, 0, safe_len - 1);
      if (clamped === last_committed.current) {
        return;
      }
      last_committed.current = clamped;
      ignore_prop_sync_until.current = Date.now() + SETTLE_MS + 80;
      if (hapticOnIndexCommit) {
        triggerHaptic('selection');
      }
      onIndexChange(clamped);
    },
    [hapticOnIndexCommit, onIndexChange, safe_len],
  );

  const finishSettle = useCallback(
    (target: number) => {
      settle_lock.value = 0;
      commitIndex(target);
    },
    [commitIndex, settle_lock],
  );

  const goToPage = useCallback(
    (index: number) => {
      if (safe_len === 0 || settle_lock.value === 1) {
        return;
      }
      const next = clamp(index, 0, safe_len - 1);
      cancelAnimation(index_sv);
      cancelAnimation(drag_x);
      drag_x.value = 0;
      settle_lock.value = 1;
      index_sv.value = withTiming(next, { duration: SETTLE_MS }, finished => {
        'worklet';
        if (finished) {
          runOnJS(finishSettle)(next);
        } else {
          settle_lock.value = 0;
        }
      });
    },
    [drag_x, finishSettle, index_sv, safe_len, settle_lock],
  );

  useEffect(() => {
    if (safe_len === 0) {
      return;
    }
    if (!has_mounted.current) {
      has_mounted.current = true;
      last_committed.current = clamped_active;
      index_sv.value = clamped_active;
      drag_x.value = 0;
      return;
    }
    if (Date.now() < ignore_prop_sync_until.current) {
      if (clamped_active === last_committed.current) {
        ignore_prop_sync_until.current = 0;
      }
      return;
    }
    if (clamped_active === last_committed.current) {
      return;
    }
    last_committed.current = clamped_active;
    cancelAnimation(index_sv);
    cancelAnimation(drag_x);
    drag_x.value = 0;
    index_sv.value = clamped_active;
  }, [clamped_active, cardWidth, safe_len, index_sv, drag_x]);

  const pan = useMemo(() => {
    return Gesture.Pan()
      .activeOffsetX([-10, 10])
      .failOffsetY([-14, 14])
      .onBegin(() => {
        'worklet';
        if (settle_lock.value === 1) {
          return;
        }
        cancelAnimation(drag_x);
        cancelAnimation(index_sv);
        drag_x.value = 0;
      })
      .onUpdate(e => {
        'worklet';
        if (settle_lock.value === 1) {
          return;
        }
        const at_start = index_sv.value <= 0.001;
        const at_end = index_sv.value >= safe_len - 1 - 0.001;
        let tx = e.translationX;
        const max_follow = cardWidth;
        if (at_start && tx > 0) {
          tx = tx * 0.35;
        } else if (at_end && tx < 0) {
          tx = tx * 0.35;
        }
        drag_x.value = clamp(tx, -max_follow, max_follow);
      })
      .onEnd(e => {
        'worklet';
        if (settle_lock.value === 1) {
          drag_x.value = 0;
          return;
        }
        const start = Math.round(index_sv.value);
        const tx = e.translationX;
        const vx = e.velocityX;

        // 1 swipe = 1 page. Direction only — distance/speed beyond threshold ignored.
        let step = 0;
        if (Math.abs(tx) >= SWIPE_COMMIT_PX) {
          step = tx < 0 ? 1 : -1;
        } else if (Math.abs(vx) >= SWIPE_VELOCITY) {
          step = vx < 0 ? 1 : -1;
        }

        const target = clamp(start + step, 0, Math.max(0, safe_len - 1));
        settle_lock.value = 1;
        drag_x.value = withTiming(0, { duration: SETTLE_MS });
        index_sv.value = withTiming(target, { duration: SETTLE_MS }, finished => {
          'worklet';
          if (finished) {
            runOnJS(finishSettle)(target);
          } else {
            settle_lock.value = 0;
          }
        });
      })
      .onFinalize((_e, success) => {
        'worklet';
        if (!success && settle_lock.value === 0) {
          drag_x.value = withTiming(0, { duration: 160 });
        }
      });
  }, [cardWidth, drag_x, finishSettle, index_sv, safe_len, settle_lock]);

  const touch_gesture = useMemo(() => {
    // Pan waits on activeOffsetX so nested Clickable2 taps (See more) can recognize first.
    return pan;
  }, [pan]);

  if (safe_len === 0) {
    return null;
  }

  const paginator_row_min_h = showPaginator ? 24 : 0;
  const paginator_gap = showPaginator ? slidePaginatorGap : 0;
  const card_left = (screen_width - cardWidth) / 2;
  const active_item = data[clamped_active];

  const root_anim_style = useAnimatedStyle(() => {
    const h = cardHeightSV != null ? cardHeightSV.value : cardHeight;
    return { minHeight: h + paginator_row_min_h + paginator_gap };
  }, [cardHeight, cardHeightSV, paginator_row_min_h, paginator_gap]);

  const stage_anim_style = useAnimatedStyle(() => {
    const h = cardHeightSV != null ? cardHeightSV.value : cardHeight;
    return { height: h, width: screen_width };
  }, [cardHeight, cardHeightSV, screen_width]);

  const touch_anim_style = useAnimatedStyle(() => {
    const h = cardHeightSV != null ? cardHeightSV.value : cardHeight;
    return { width: screen_width, height: h };
  }, [cardHeight, cardHeightSV, screen_width]);

  const overlay_slot_anim_style = useAnimatedStyle(() => {
    const h = cardHeightSV != null ? cardHeightSV.value : cardHeight;
    return { left: card_left, width: cardWidth, height: h };
  }, [cardHeight, cardHeightSV, card_left, cardWidth]);

  return (
    <Animated.View
      style={[
        layoutStyles.root,
        layoutLtrStyle,
        styles.container,
        cardHeightSV == null
          ? { minHeight: cardHeight + paginator_row_min_h + paginator_gap }
          : null,
        root_anim_style,
        style,
      ]}>
      <Animated.View
        style={[
          layoutLtrStyle,
          styles.slideStage,
          cardHeightSV == null ? { height: cardHeight, width: screen_width } : { width: screen_width },
          stage_anim_style,
        ]}>
        {data.map((item, index) => {
          if (Math.abs(index - clamped_active) > NEIGHBOR_WINDOW) {
            return null;
          }
          return (
            <AnimatedStackedCard
              key={keyExtractor(item, index)}
              index={index}
              progress={progress}
              cardWidth={cardWidth}
              cardHeight={cardHeight}
              cardHeightSV={cardHeightSV}
              cardLeft={card_left}
              cardShellStyle={cardShellStyle}>
              {renderCard(item, index)}
            </AnimatedStackedCard>
          );
        })}

        <GestureDetector gesture={touch_gesture}>
          <Animated.View
            style={[
              styles.touchLayer,
              cardHeightSV == null
                ? {
                    width: screen_width,
                    height: cardHeight,
                  }
                : { width: screen_width },
              touch_anim_style,
              listStyle,
            ]}>
            <Animated.View
              style={[
                styles.overlaySlot,
                cardHeightSV == null
                  ? { left: card_left, width: cardWidth, height: cardHeight }
                  : { left: card_left, width: cardWidth },
                overlay_slot_anim_style,
              ]}
              pointerEvents="box-none">
              {renderScrollOverlay && active_item != null
                ? renderScrollOverlay(active_item, clamped_active)
                : null}
            </Animated.View>
          </Animated.View>
        </GestureDetector>
      </Animated.View>

      {showPaginator ? (
        <View style={[styles.paginatorRow, { marginTop: paginator_gap, minHeight: paginator_row_min_h }, paginatorRowStyle]}>
          <Paginator
            pagesAmount={safe_len}
            currentPageIndex={progress}
            visibleDots={paginatorVisibleDots}
            dotSize={paginatorDotSize}
            spacing={paginatorSpacing}
            onDotPress={goToPage}
          />
        </View>
      ) : null}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 0,
    width: '100%',
  },
  slideStage: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  touchLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: TOUCH_LAYER_Z,
    ...Platform.select({
      android: { elevation: TOUCH_LAYER_Z },
      default: {},
    }),
  },
  overlaySlot: {
    position: 'absolute',
    top: 0,
  },
  paginatorRow: {
    width: '100%',
    minHeight: 24,
    paddingTop: 0,
    paddingBottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: TOUCH_LAYER_Z + 1,
  },
});
